import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PortalLoadingCard, PortalStateCard, formatPortalDateTime } from './portalShared'

const THREAD_TYPE_LABELS = {
  vendor: 'All crews',
  crew: 'Crew',
  direct: 'Direct',
  job: 'Job',
  announcement: 'Announcement'
}

const useStyles = makeStyles((theme) => ({
  layout: {
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  card: {
    borderRadius: 'var(--df-radius-md)',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  threadList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  threadButton: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing(1, 1.5),
    borderRadius: 0,
    borderBottom: '1px solid var(--df-line)',
    borderLeft: '2px solid transparent',
    background: 'transparent'
  },
  activeThread: {
    borderLeftColor: 'var(--df-accent)',
    background: 'var(--df-surface-2)'
  },
  threadTitle: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  threadMeta: {
    color: 'var(--df-ink-3)'
  },
  messagesWrap: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 560
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  messageList: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: 520,
    overflowY: 'auto',
    paddingRight: theme.spacing(1)
  },
  messageBubble: {
    borderRadius: 'var(--df-radius-lg)',
    padding: theme.spacing(2),
    border: '1px solid var(--df-line)',
    background: 'var(--df-surface)',
    maxWidth: '80%'
  },
  messageBubbleOwn: {
    marginLeft: 'auto',
    background: 'var(--df-primary-soft)',
    borderColor: '#b8d4c2'
  },
  messageAuthor: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  messageMeta: {
    color: 'var(--df-ink-3)',
    marginTop: theme.spacing(0.25)
  },
  messageBody: {
    color: 'var(--df-ink)',
    whiteSpace: 'pre-wrap',
    marginTop: theme.spacing(1.5)
  },
  attachmentRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 8
  },
  attachmentThumb: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: 'var(--df-radius-sm)',
    cursor: 'pointer',
    border: '1px solid var(--df-line)',
    '&:hover': {
      opacity: 0.85
    }
  },
  composeRow: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-end'
  },
  composeInput: {
    flex: 1
  },
  muted: {
    color: 'var(--df-ink-3)'
  },
  pillRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1.5)
  }
}))

function formatThreadSubtitle (thread) {
  if (thread.subtitle) return thread.subtitle
  if (thread.type === 'crew') return 'Crew-scoped conversation'
  if (thread.type === 'direct') return 'Direct thread'
  if (thread.type === 'job') return 'Job thread'
  return 'Vendor-wide conversation'
}

const SubcontractorMessages = ({ user, notification }) => {
  const classes = useStyles()
  const history = useHistory()
  const match = useRouteMatch()
  const params = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [threads, setThreads] = useState([])
  const [messages, setMessages] = useState([])
  const [messageText, setMessageText] = useState('')
  const [sending, setSending] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [threadTargets, setThreadTargets] = useState({ crews: [], users: [] })
  const [createForm, setCreateForm] = useState({
    type: 'vendor',
    crewId: '',
    employeeId: '',
    relatedJobId: '',
    relatedJobLabel: ''
  })

  const selectedThreadId = params.threadId || ''
  const currentUrl = match?.url || '/subcontractor/messages'
  const baseUrl = selectedThreadId && currentUrl.endsWith(`/${selectedThreadId}`)
    ? currentUrl.slice(0, currentUrl.length - selectedThreadId.length - 1)
    : currentUrl
  const profile = user?.subcontractorProfile || {}
  const isAdmin = profile.role === 'admin'

  const selectedThread = useMemo(
    () => threads.find(item => String(item._id) === String(selectedThreadId || '')) || null,
    [selectedThreadId, threads]
  )

  const loadThreads = useCallback(async (requestedThreadId = null) => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get('/api/subcontractor/v2/threads')
      const nextThreads = response.data?.items || []
      setThreads(nextThreads)

      const targetThreadId = requestedThreadId || selectedThreadId
      const chosenThread = nextThreads.find(item => String(item._id) === String(targetThreadId || '')) || nextThreads[0]
      if (chosenThread?._id) {
        if (String(chosenThread._id) !== String(selectedThreadId || '')) {
          history.replace(`${baseUrl}/${chosenThread._id}`)
        }
      } else if (selectedThreadId) {
        history.replace(baseUrl)
      }
    } catch (requestError) {
      console.error('Failed to load subcontractor V2 threads', requestError)
      setError(requestError?.response?.data?.error || 'Unable to load subcontractor conversations right now.')
    } finally {
      setLoading(false)
    }
  }, [baseUrl, history, selectedThreadId])

  useEffect(() => {
    loadThreads()
  }, [loadThreads])

  const loadThreadTargets = useCallback(async () => {
    if (!isAdmin) return
    try {
      const [crewResponse, userResponse] = await Promise.all([
        axios.get('/api/subcontractor/crews'),
        axios.get('/api/subcontractor/active-users')
      ])
      setThreadTargets({
        crews: (crewResponse.data?.items || []).filter(item => item.isActive !== false),
        users: (userResponse.data?.items || []).filter(item => item.accessStatus !== 'offboarded')
      })
    } catch (requestError) {
      console.error('Failed to load thread targets', requestError)
    }
  }, [isAdmin])

  useEffect(() => {
    loadThreadTargets()
  }, [loadThreadTargets])

  const loadMessages = useCallback(async (threadId) => {
    if (!threadId) {
      setMessages([])
      return
    }

    setLoadingMessages(true)
    try {
      const response = await axios.get(`/api/subcontractor/v2/threads/${threadId}/messages`)
      setMessages(response.data?.items || [])
      await axios.post(`/api/subcontractor/v2/threads/${threadId}/read`)
      setThreads((current) => current.map((thread) => (
        String(thread._id) === String(threadId)
          ? { ...thread, unreadCount: 0 }
          : thread
      )))
    } catch (requestError) {
      console.error('Failed to load thread messages', requestError)
      notification?.({
        type: 'danger',
        title: 'Messages Unavailable',
        message: requestError?.response?.data?.error || 'Unable to load conversation messages.'
      })
    } finally {
      setLoadingMessages(false)
    }
  }, [notification])

  useEffect(() => {
    if (selectedThreadId) {
      loadMessages(selectedThreadId)
    }
  }, [loadMessages, selectedThreadId])

  const handleCreateThread = async () => {
    try {
      const payload = {
        type: createForm.type
      }
      if (createForm.crewId) payload.crewId = createForm.crewId
      if (createForm.employeeId) payload.employeeId = createForm.employeeId
      if (createForm.relatedJobId) payload.relatedJobId = createForm.relatedJobId.trim()
      if (createForm.relatedJobLabel) payload.relatedJobLabel = createForm.relatedJobLabel.trim()

      const response = await axios.post('/api/subcontractor/v2/threads', payload)
      const threadId = response.data?.thread?._id
      setComposerOpen(false)
      setCreateForm({
        type: 'vendor',
        crewId: '',
        employeeId: '',
        relatedJobId: '',
        relatedJobLabel: ''
      })
      await loadThreads(threadId)
      notification?.({
        type: 'success',
        title: 'Conversation Ready',
        message: 'The conversation thread is ready for use.'
      })
    } catch (requestError) {
      console.error('Failed to create subcontractor thread', requestError)
      notification?.({
        type: 'danger',
        title: 'Create Failed',
        message: requestError?.response?.data?.error || 'Unable to create that conversation.'
      })
    }
  }

  const handleSend = async () => {
    if (!selectedThread?._id || !messageText.trim()) return

    setSending(true)
    try {
      await axios.post(`/api/subcontractor/v2/threads/${selectedThread._id}/messages`, {
        body: messageText.trim(),
        relatedJobId: selectedThread.relatedJobId || undefined
      })
      setMessageText('')
      await Promise.all([
        loadMessages(selectedThread._id),
        loadThreads(selectedThread._id)
      ])
    } catch (requestError) {
      console.error('Failed to send subcontractor message', requestError)
      notification?.({
        type: 'danger',
        title: 'Send Failed',
        message: requestError?.response?.data?.error || 'Unable to send that message.'
      })
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return <PortalLoadingCard title="Loading messages" message="Loading your conversations..." minHeight={280} />
  }

  if (error) {
    return <PortalStateCard title="Messages unavailable" message={error} actionLabel="Retry" onAction={loadThreads} tone="danger" minHeight={280} />
  }

  return (
    <>
      <div className={classes.layout}>
        <Card className={classes.card}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gridGap={12}>
              <div>
                <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--df-ink)' }}>Conversations</Typography>
                <Typography variant="body2" className={classes.muted}>
                  Vendor, crew, direct, and job-scoped threads.
                </Typography>
              </div>
              {isAdmin ? (
                <Button variant="outlined" size="small" onClick={() => setComposerOpen(true)}>
                  New thread
                </Button>
              ) : null}
            </Box>

            <div className={classes.threadList}>
              {threads.map((thread) => (
                <Button
                  key={thread._id}
                  className={`${classes.threadButton} ${String(thread._id) === String(selectedThreadId || '') ? classes.activeThread : ''}`}
                  onClick={() => history.push(`${baseUrl}/${thread._id}`)}
                >
                  <Box width="100%">
                    <Box display="flex" justifyContent="space-between" alignItems="center" gridGap={8}>
                      <Typography variant="subtitle2" className={classes.threadTitle}>
                        {thread.title || 'Conversation'}
                      </Typography>
                      {thread.unreadCount > 0 ? (
                        <Chip size="small" label={thread.unreadCount} className="df-chip-info" />
                      ) : null}
                    </Box>
                    <Box mt={0.75}>
                      <Typography variant="body2" className={classes.threadMeta}>
                        {THREAD_TYPE_LABELS[thread.type] || thread.type}
                        {thread.lastMessageAt ? ` - ${formatPortalDateTime(thread.lastMessageAt)}` : ''}
                      </Typography>
                      <Typography variant="body2" className={classes.threadMeta}>
                        {thread.lastMessagePreview || formatThreadSubtitle(thread)}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
              ))}

              {!threads.length ? (
                <PortalStateCard title="No conversations yet" message={'No conversations yet. Start one using the "New Thread" button.'} minHeight={220} />
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card className={classes.card}>
          <CardContent className={classes.messagesWrap}>
            {!selectedThread ? (
              <PortalStateCard title="Select a conversation" message="Select a conversation to view messages and reply." minHeight={420} />
            ) : (
              <>
                <div className={classes.headerRow}>
                  <div>
                    <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--df-ink)' }}>
                      {selectedThread.title || 'Conversation'}
                    </Typography>
                    <Typography variant="body2" className={classes.muted}>
                      {formatThreadSubtitle(selectedThread)}
                    </Typography>
                    <div className={classes.pillRow}>
                      <Chip size="small" label={THREAD_TYPE_LABELS[selectedThread.type] || selectedThread.type} className="df-chip-info" />
                      {selectedThread.relatedJobId ? <Chip size="small" label={`Job ${selectedThread.relatedJobLabel || selectedThread.relatedJobId}`} className="df-chip-info" /> : null}
                    </div>
                  </div>
                  <Button variant="text" onClick={() => loadMessages(selectedThread._id)} disabled={loadingMessages}>
                    {loadingMessages ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </div>

                <div className={classes.messageList}>
                  {messages.map((message) => {
                    const isOwnMessage = String(message.author?.userId || '') === String(user?._id || '')
                    return (
                      <div
                        key={message._id}
                        className={`${classes.messageBubble} ${isOwnMessage ? classes.messageBubbleOwn : ''}`}
                      >
                        <Typography variant="subtitle2" className={classes.messageAuthor}>
                          {message.author?.displayName || (message.author?.side === 'office' ? 'HMI Office' : 'Subcontractor')}
                        </Typography>
                        <Typography variant="caption" className={classes.messageMeta}>
                          {formatPortalDateTime(message.createdAt)}
                          {message.priority === 'urgent' ? ' - Urgent' : ''}
                        </Typography>
                        <Typography variant="body2" className={classes.messageBody}>
                          {message.body}
                        </Typography>
                        {Array.isArray(message.attachments) && message.attachments.filter(a => a.type === 'photo' && a.url).length > 0 && (
                          <div className={classes.attachmentRow}>
                            {message.attachments.filter(a => a.type === 'photo' && a.url).map((attachment, idx) => (
                              <img
                                key={idx}
                                src={attachment.url}
                                alt={attachment.fileName || 'Photo'}
                                className={classes.attachmentThumb}
                                onClick={() => window.open(attachment.url, '_blank')}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {!loadingMessages && !messages.length ? (
                    <PortalStateCard title="No messages yet" message="No messages yet. Type below to start the conversation." minHeight={260} />
                  ) : null}
                </div>

                <div className={classes.composeRow}>
                  <TextField
                    className={classes.composeInput}
                    variant="outlined"
                    label="Reply"
                    multiline
                    minRows={3}
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value)}
                    placeholder="Type your message..."
                  />
                  <Button variant="contained" color="primary" onClick={handleSend} disabled={sending || !messageText.trim()}>
                    {sending ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={composerOpen} onClose={() => setComposerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Conversation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Thread type"
                value={createForm.type}
                onChange={(event) => setCreateForm((current) => ({ ...current, type: event.target.value, crewId: '', employeeId: '' }))}
              >
                <MenuItem value="vendor">Vendor-wide</MenuItem>
                <MenuItem value="crew">Crew</MenuItem>
                <MenuItem value="direct">Direct user</MenuItem>
                <MenuItem value="job">Job</MenuItem>
              </TextField>
            </Grid>

            {createForm.type === 'crew' ? (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="Crew"
                  value={createForm.crewId}
                  onChange={(event) => setCreateForm((current) => ({ ...current, crewId: event.target.value }))}
                >
                  {threadTargets.crews.map((crew) => (
                    <MenuItem key={crew._id} value={crew._id}>{crew.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {createForm.type === 'direct' ? (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  label="User"
                  value={createForm.employeeId}
                  onChange={(event) => setCreateForm((current) => ({ ...current, employeeId: event.target.value }))}
                >
                  {threadTargets.users.map((item) => (
                    <MenuItem key={item.employeeId} value={item.employeeId}>{item.displayName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {createForm.type === 'job' ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Related job id"
                    value={createForm.relatedJobId}
                    onChange={(event) => setCreateForm((current) => ({ ...current, relatedJobId: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Job label"
                    value={createForm.relatedJobLabel}
                    onChange={(event) => setCreateForm((current) => ({ ...current, relatedJobLabel: event.target.value }))}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposerOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleCreateThread}
            disabled={
              (createForm.type === 'crew' && !createForm.crewId) ||
              (createForm.type === 'direct' && !createForm.employeeId) ||
              (createForm.type === 'job' && !createForm.relatedJobId.trim())
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorMessages)
